import { Plus, Sparkles, Trash2, GitMerge } from 'lucide-react'
import { cn } from '@/lib/utils'
import type {
  ValuePropositionCanvas,
  PersonaWorkshopSession,
  PersonaData,
  JtbdData,
  VPCStatus,
} from '@ispot/shared'
import { migratePersonaData } from '@ispot/shared'
import { PersonaPortrait } from '@/components/requirements/cells'

interface Props {
  vpcs: ValuePropositionCanvas[]
  sessions: PersonaWorkshopSession[]
  onOpen: (vpc: ValuePropositionCanvas) => void
  onNew: () => void
  onDelete: (id: string) => void
}

const STATUS: Record<VPCStatus, { label: string; pip: string }> = {
  in_progress: { label: 'In progress', pip: 'var(--color-accent)' },
  complete:    { label: 'Complete',    pip: 'var(--color-success)' },
  draft:       { label: 'Draft',       pip: 'var(--color-border-strong)' },
}
const SECTION_ORDER: VPCStatus[] = ['in_progress', 'complete', 'draft']

interface Tally {
  painsTackled: number
  painsTotal: number
  gainsDelivered: number
  gainsTotal: number
  unaddressed: number
}

function computeTally(vpc: ValuePropositionCanvas, jtbd: JtbdData | null): Tally {
  const painsTotal = jtbd?.pains.length ?? 0
  const gainsTotal = jtbd?.gains.length ?? 0
  const tackled = new Set<string>()
  const delivered = new Set<string>()
  vpc.data.painRelievers
    .filter(r => r.description?.trim().length)
    .forEach(r => {
      if (r.painId) tackled.add(r.painId)
      r.painIds?.forEach(id => tackled.add(id))
    })
  vpc.data.gainCreators
    .filter(c => c.description?.trim().length)
    .forEach(c => {
      if (c.gainId) delivered.add(c.gainId)
      c.gainIds?.forEach(id => delivered.add(id))
    })
  return {
    painsTackled: tackled.size,
    painsTotal,
    gainsDelivered: delivered.size,
    gainsTotal,
    unaddressed: Math.max(0, painsTotal - tackled.size) + Math.max(0, gainsTotal - delivered.size),
  }
}

function timeAgo(iso: string): string {
  const ms = Date.now() - new Date(iso).getTime()
  const m = Math.floor(ms / 60_000)
  if (m < 1) return 'just now'
  if (m < 60) return `${m}m ago`
  const h = Math.floor(m / 60)
  if (h < 24) return `${h}h ago`
  const d = Math.floor(h / 24)
  if (d === 1) return 'Yesterday'
  if (d < 30) return `${d}d ago`
  const mo = Math.floor(d / 30)
  return mo < 12 ? `${mo}mo ago` : `${Math.floor(mo / 12)}y ago`
}

export function ValueDesignList({ vpcs, sessions, onOpen, onNew, onDelete }: Props) {
  const enriched = vpcs.map(vpc => {
    const session = sessions.find(s => s.id === vpc.personaSessionId)
    const persona = session ? migratePersonaData(session.personaData as PersonaData) : null
    const jtbd = persona?.jtbds?.find(j => j.id === vpc.jtbdId) ?? null
    return { vpc, persona, jtbd, tally: computeTally(vpc, jtbd) }
  })

  const groups: Record<VPCStatus, typeof enriched> = { in_progress: [], complete: [], draft: [] }
  enriched.forEach(e => groups[e.vpc.status].push(e))

  const totals = enriched.reduce(
    (acc, e) => ({
      painsTackled: acc.painsTackled + e.tally.painsTackled,
      painsTotal: acc.painsTotal + e.tally.painsTotal,
      gainsDelivered: acc.gainsDelivered + e.tally.gainsDelivered,
      gainsTotal: acc.gainsTotal + e.tally.gainsTotal,
    }),
    { painsTackled: 0, painsTotal: 0, gainsDelivered: 0, gainsTotal: 0 },
  )

  return (
    <div className="max-w-4xl mx-auto px-12 py-10">
      <header className="flex items-end justify-between gap-6 pb-5 border-b border-[var(--color-border)]">
        <div className="min-w-0">
          <h1 className="text-2xl font-display font-semibold text-[var(--color-text)] tracking-tight leading-tight">
            Value design
          </h1>
          <p className="text-[13px] text-[var(--color-text-secondary)] mt-1.5 max-w-2xl">
            Map customer pains and gains to your solution, one persona × job-to-be-done at a time.
            {totals.painsTotal + totals.gainsTotal > 0 && (
              <>
                <span className="text-[var(--color-border-strong)] mx-2">·</span>
                <span className="text-[var(--color-text)] font-medium tabular-nums">
                  {totals.painsTackled}/{totals.painsTotal} pains tackled
                </span>
                <span className="text-[var(--color-border-strong)] mx-2">·</span>
                <span className="text-[var(--color-text)] font-medium tabular-nums">
                  {totals.gainsDelivered}/{totals.gainsTotal} outcomes delivered
                </span>
              </>
            )}
          </p>
        </div>
        <button
          onClick={onNew}
          className="shrink-0 inline-flex items-center gap-1.5 h-8 px-3 rounded-md text-xs font-medium bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-700)] transition-colors"
        >
          <Plus className="w-3.5 h-3.5" /> New design
        </button>
      </header>

      {vpcs.length === 0 ? (
        <EmptyState onNew={onNew} />
      ) : (
        <div>
          {SECTION_ORDER.map(status => {
            const items = groups[status]
            if (items.length === 0) return null
            return (
              <section key={status} className="mt-7">
                <SectionEyebrow label={STATUS[status].label} count={items.length} />
                <div>
                  {items.map(({ vpc, persona, jtbd, tally }) => (
                    <Row
                      key={vpc.id}
                      vpc={vpc}
                      personaName={persona?.name ?? 'Unknown persona'}
                      personaPhoto={persona?.photo}
                      personaRole={persona?.role}
                      personaArchetype={persona?.archetype}
                      jtbdTitle={jtbd?.title ?? vpc.jtbdId}
                      tally={tally}
                      onOpen={() => onOpen(vpc)}
                      onDelete={() => onDelete(vpc.id)}
                    />
                  ))}
                </div>
              </section>
            )
          })}
        </div>
      )}
    </div>
  )
}

function SectionEyebrow({ label, count }: { label: string; count: number }) {
  return (
    <div className="flex items-center gap-2.5 pb-2">
      <span className="text-[10.5px] font-semibold uppercase tracking-[0.14em] text-[var(--color-text-muted)]">
        {label}
      </span>
      <span className="text-[11px] font-mono text-[var(--color-text-secondary)] tabular-nums">{count}</span>
      <span className="flex-1 h-px bg-[var(--color-border)]" />
    </div>
  )
}

interface RowProps {
  vpc: ValuePropositionCanvas
  personaName: string
  personaPhoto?: string
  personaRole?: string
  personaArchetype?: string
  jtbdTitle: string
  tally: Tally
  onOpen: () => void
  onDelete: () => void
}

function Row({ vpc, personaName, personaPhoto, personaRole, personaArchetype, jtbdTitle, tally, onOpen, onDelete }: RowProps) {
  return (
    <div
      onClick={onOpen}
      className="group grid items-center gap-x-5 px-3 py-3.5 rounded-md border-b border-[var(--color-border)] last:border-b-0 cursor-pointer hover:bg-[var(--color-warm-100)] transition-colors"
      style={{ gridTemplateColumns: '44px minmax(0,1fr) auto auto auto' }}
    >
      <PersonaPortrait name={personaName} photo={personaPhoto} size={40} />

      <div className="min-w-0 flex flex-col gap-1">
        <div className="flex items-baseline gap-2 min-w-0">
          <span className="font-semibold text-[14.5px] text-[var(--color-text)] tracking-[-0.005em] shrink-0">
            {personaName}
          </span>
          <span className="text-[var(--color-text-muted)] font-normal">—</span>
          <span className="text-[14.5px] text-[var(--color-text)] tracking-[-0.005em] truncate">{jtbdTitle}</span>
        </div>
        <div className="text-[12px] text-[var(--color-text-muted)] truncate">
          {personaRole && <span className="text-[var(--color-text-secondary)]">{personaRole}</span>}
          {personaRole && personaArchetype && <span className="text-[var(--color-border-strong)] mx-2">·</span>}
          {personaArchetype && <em className="not-italic before:content-['“'] after:content-['”']">{personaArchetype}</em>}
          {vpc.isOrphaned && (
            <>
              <span className="text-[var(--color-border-strong)] mx-2">·</span>
              <span className="text-[var(--color-warning-fg)]">Orphaned — persona or job removed</span>
            </>
          )}
        </div>
      </div>

      <TallyStrip tally={tally} />

      <div className="flex items-center gap-3 shrink-0">
        <Status status={vpc.status} />
        <span className="text-[11.5px] text-[var(--color-text-muted)] tabular-nums w-20 text-right">
          {timeAgo(vpc.updatedAt)}
        </span>
      </div>

      <button
        onClick={e => {
          e.stopPropagation()
          onDelete()
        }}
        aria-label="Delete value design"
        className="opacity-0 group-hover:opacity-100 p-1.5 rounded text-[var(--color-text-muted)] hover:bg-[var(--color-danger-bg)] hover:text-[var(--color-danger-fg)] transition-all shrink-0"
      >
        <Trash2 className="w-3.5 h-3.5" />
      </button>
    </div>
  )
}

function TallyStrip({ tally }: { tally: Tally }) {
  if (tally.painsTotal === 0 && tally.gainsTotal === 0) {
    return <span className="text-[11px] font-mono text-[var(--color-text-muted)]">no job mapped</span>
  }
  return (
    <div className="flex items-center gap-3.5 font-mono text-[11px] text-[var(--color-text-muted)] tabular-nums shrink-0">
      <span className="inline-flex items-center gap-1.5">
        <span className="text-[var(--color-solution-700)] font-semibold">→</span>
        <span>
          <strong className="font-semibold text-[var(--color-text)]">{tally.painsTackled}</strong>/{tally.painsTotal} pains
        </span>
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="text-[var(--color-market-700)] font-semibold">→</span>
        <span>
          <strong className="font-semibold text-[var(--color-text)]">{tally.gainsDelivered}</strong>/{tally.gainsTotal} outcomes
        </span>
      </span>
      {tally.unaddressed > 0 && (
        <span className="inline-flex items-center gap-1.5 text-[var(--color-accent)] font-semibold">
          <span className="w-1.5 h-1.5 rounded-full bg-[var(--color-accent)]" />
          {tally.unaddressed} unaddressed
        </span>
      )}
    </div>
  )
}

function Status({ status }: { status: VPCStatus }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-[12px] font-medium text-[var(--color-text-secondary)] w-24">
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: STATUS[status].pip }} />
      {STATUS[status].label}
    </span>
  )
}

function EmptyState({ onNew }: { onNew: () => void }) {
  return (
    <div className="text-center py-20">
      <div className="w-12 h-12 mx-auto mb-4 rounded-full bg-[var(--color-warm-100)] flex items-center justify-center">
        <GitMerge className="w-6 h-6 text-[var(--color-text-muted)]" />
      </div>
      <h2 className="text-base font-medium text-[var(--color-text)] mb-1">No value designs yet</h2>
      <p className="text-sm text-[var(--color-text-muted)] mb-6 max-w-sm mx-auto">
        Bridge customer understanding with solution design. You'll need at least one completed persona with a ready job-to-be-done.
      </p>
      <button
        onClick={onNew}
        className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md text-sm font-medium bg-[var(--color-accent)] text-white hover:bg-[var(--color-accent-700)] transition-colors"
      >
        <Sparkles className="w-3.5 h-3.5" /> Start your first design
      </button>
    </div>
  )
}
